import { PROJECT_NAME, COMPANY_EMAIL, PROJECT_YEAR, PROJECT_COPYRIGHT } from '@shared/constants';

/**
 * @group Layout Components
 *
 * @summary
 * <blockquote>Footer component displaying legal links, support contact, and copyright information.</blockquote>
 *
 * @example
 * ```typescript
 * import { SiteFooterLegal } from '@/components/layout';
 *
 * export function Layout() {
 *   return (
 *     <div>
 *       <main>{children}</main>
 *       <SiteFooterLegal />
 *     </div>
 *   );
 * }
 * ```
 */

interface SiteFooterLegalProps {
  /** Optional CSS class for custom styling */
  className?: string;
}

/**
 * Renders legal footer section with privacy policy link, terms link,
 * about link, support email, and copyright notice.
 */
export function SiteFooterLegal({ className = '' }: SiteFooterLegalProps) {
  const currentYear = PROJECT_YEAR;

  return (
    <footer className={`bg-gray-900 text-gray-300 ${className}`}>
      <nav aria-label="Footer Navigation" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Legal Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="/privacy"
                  className="hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="/terms"
                  className="hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                >
                  Terms of Service
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  className="hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                >
                  About Us
                </a>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-white font-semibold mb-4">Support</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href={`mailto:${COMPANY_EMAIL}`}
                  className="hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                  aria-label={`Contact us at ${COMPANY_EMAIL}`}
                >
                  Email Support
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  className="hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                >
                  Contact Info
                </a>
              </li>
            </ul>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-white font-semibold mb-4">Product</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="/"
                  className="hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  className="hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                >
                  Features
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700 mt-8 pt-8">
          {/* Copyright */}
          <div className="text-center text-sm text-gray-400">
            <p>
              &copy; {currentYear} {PROJECT_COPYRIGHT}
            </p>
            <p className="mt-2">
              {PROJECT_NAME} is committed to protecting your privacy. Please see our{' '}
              <a href="/privacy" className="text-blue-400 hover:text-blue-300">
                Privacy Policy
              </a>{' '}
              for more information.
            </p>
          </div>
        </div>
      </nav>
    </footer>
  );
}
